/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  AttackVolumeByCountry,
  NetworkAttackByThreatEvents,
  NetworkAttacksByCountry,
  ThreatTypeTrend,
} from '../types/StatTypes'
import { countryNameToCode } from './malware-mapper'

export const mapIntelToNetworkAttacksMap = (apiData: NetworkAttacksByCountry[]) => {
  let csvString = 'Country,Code,Threat Events\n'

  apiData.forEach((item) => {
    const countryCode = countryNameToCode[item.country] || 'N/A'
    if (countryCode !== 'N/A') {
      csvString += `${item.country},${countryCode},${item.threat_count}\n`
    }
  })
  return csvString
}

export const mapIntelToAttackByThreatEvent = (threatsByDevice: NetworkAttackByThreatEvents[]) => {
  return threatsByDevice
    .filter((threatByDevice) => threatByDevice.threat_type !== 'Unknown')
    .map((threatByDevice) => [threatByDevice.threat_type, threatByDevice.total_threats])
}

export const mapIntelToNetworkThreatsLineChart = (
  apiData: { month: string; network_type: string; threat_count: number }[]
): { categories: string[]; series: any[] } => {
  const months = [...new Set(apiData.map((entry) => entry.month.slice(0, 7)))].sort()
  const networkTypes = [...new Set(apiData.map((entry) => entry.network_type))]

  const series = networkTypes.map((network) => ({
    name: network,
    type: 'line',
    data: months.map((month) => {
      const dataPoint = apiData.find((entry) => entry.month.slice(0, 7) === month && entry.network_type === network)
      return dataPoint ? dataPoint.threat_count : null
    }),
  }))

  return { categories: months, series }
}

export const mapIntelToThreatTypesTrendLineChart = (
  apiData: ThreatTypeTrend[]
): { categories: string[]; series: any[] } => {
  const months = [...new Set(apiData.map((entry) => entry.month.slice(0, 7)))].sort()
  const threatTypes = [...new Set(apiData.map((entry) => entry.threat_type))]

  const series = threatTypes.map((threat) => ({
    name: threat,
    type: 'line',
    data: months.map((month) => {
      const dataPoint = apiData.find((entry) => entry.month.slice(0, 7) === month && entry.threat_type === threat)
      return dataPoint ? dataPoint.total_threats : null
    }),
  }))

  return { categories: months, series }
}

export const mapIntelToSumOfAttacksHeatmap = (
  apiData: { month: string; total_attacks: number }[]
): { xAxis: string[]; yAxis: string[]; heatmapData: number[][] } => {
  const years = [...new Set(apiData.map((entry) => entry.month.slice(0, 4)))].sort()
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const heatmapData = apiData.map((entry) => {
    const monthIndex = parseInt(entry.month.slice(5, 7)) - 1
    const yearIndex = years.indexOf(entry.month.slice(0, 4))
    return [monthIndex, yearIndex, entry.total_attacks]
  })

  return { xAxis: months, yAxis: years, heatmapData }
}

export const mapIntelForAttackVolumeCountries = (threatsByDevice: AttackVolumeByCountry[]) => {
  return threatsByDevice
    .filter((threatByDevice) => threatByDevice.country !== 'Unknown')
    .map((threatByDevice) => [threatByDevice.country, threatByDevice.attack_volume])
}

export const mapIntelToAttackVolumeByCountryPerMonth = (
  apiData: { month: string; country: string; attack_volume: number }[]
): { xAxis: string[]; yAxis: string[]; heatmapData: any[] } => {
  // Extract unique sorted months and countries
  const months = [...new Set(apiData.map((entry) => entry.month.slice(0, 7)))].sort()
  const countries = [...new Set(apiData.map((entry) => entry.country))].sort()

  // Map data into heatmap format: [x, y, value]
  const heatmapData = apiData
    .filter((entry) => entry.attack_volume > 0) // Ignore zero-volume attacks
    .map((entry) => {
      const x = months.indexOf(entry.month.slice(0, 7)) // X-axis: Month index
      const y = countries.indexOf(entry.country) // Y-axis: Country index
      return [x, y, entry.attack_volume] // Heatmap expects this format
    })

  return { xAxis: months, yAxis: countries, heatmapData }
}

export const mapIntelToThreatTypeByCountryHeatmap = (
  apiData: { country: string; threat_type: string; attack_volume: number }[]
): { xAxis: string[]; yAxis: string[]; heatmapData: number[][] } => {
  // Extract unique threat types (X-axis)
  const threatTypes = [...new Set(apiData.map((entry) => entry.threat_type))]

  // Extract unique countries (Y-axis)
  const countries = [...new Set(apiData.map((entry) => entry.country))]

  // Convert API data into heatmap format [x, y, value]
  const heatmapData = apiData
    .map((entry) => {
      const x = threatTypes.indexOf(entry.threat_type) // X index (Threat Type)
      const y = countries.indexOf(entry.country) // Y index (Country)
      return x !== -1 && y !== -1 ? [x, y, entry.attack_volume] : null
    })
    .filter((item) => item !== null) // Remove invalid entries

  return { xAxis: threatTypes, yAxis: countries, heatmapData }
}
